// Session tranche A(docs/session-navigation-ux-spec.md §A) — 선제 세션 갱신 스케줄러.
// 순수 모듈: 시계·타이머·refresh·onExpired 를 주입받는다(node 회귀 테스트가 직접 구동 —
// tests/session-refresh-scheduler.test.mjs). credential 값도 Google 오류도 여기서 다루지 않는다
// (§A-7 원시 credential·오류 로깅 0) — 만료 시각(ms)과 성공/실패만 본다.
//
// 계약:
//  - schedule(): exp-5분에 단일 timer 1개. 이미 임계값 안이면 즉시 1회 갱신.
//  - resume(): visibilitychange/focus/online 재개용. 임계값 안이면 같은 single-flight 갱신, 밖이면 재예약만.
//  - 실패: 토큰이 아직 유효하면 앱을 막지 않고 bounded backoff 재시도(만료 시각을 넘겨 예약하지 않음).
//          실제 만료 시각 이후의 실패에서만 onExpired().
//  - stop(): 명시적 logout·unmount·새 login. timer 해제 + 진행 중 갱신의 완료를 무효화.
export const REFRESH_LEAD_MS = 5 * 60_000;
export const BACKOFF_BASE_MS = 30_000;
export const BACKOFF_MAX_MS = 4 * 60_000;
export const BACKOFF_MAX_ATTEMPTS = 5;
// 갱신 직후에도 exp 가 임계값 안이면(짧은 수명 토큰·시계 스큐) 즉시 재실행하지 않는다 — refresh storm 차단
export const MIN_RESCHEDULE_MS = 60_000;

export function computeRefreshDelay(expMs, nowMs, leadMs = REFRESH_LEAD_MS) {
  if (!Number.isFinite(expMs) || !Number.isFinite(nowMs)) return null;
  return Math.max(0, expMs - leadMs - nowMs);
}

// 갱신 자체의 전역 single-flight. timer·visibility/focus/online resume·apiClient 401 이 모두 같은 진행
// promise 를 공유해야 GIS prompt 가 두 번 뜨지 않는다(스케줄러 내부 inFlight 만으로는 401 경로를 못 묶는다).
//  run(factory): 진행 중이면 같은 promise, 아니면 factory() 로 새 갱신 시작.
//  reset(): logout·새 login — 이후 호출은 이전 진행 promise 에 합류하지 않는다.
export function createSingleFlight() {
  let current = null;
  return {
    // factory 는 동기 호출한다 — 갱신(GIS prompt) 시작을 microtask 뒤로 미루면 그 틈에 다른 트리거가
    //  두 번째 갱신을 시작할 수 있다.
    run(factory) {
      if (current) return current;
      let started;
      try {
        started = Promise.resolve(factory());
      } catch (error) {
        return Promise.reject(error);
      }
      let chained;
      chained = started.finally(() => {
        if (current === chained) current = null;
      });
      current = chained;
      return chained;
    },
    reset() {
      current = null;
    },
  };
}

export function computeBackoffDelay(
  attempt,
  baseMs = BACKOFF_BASE_MS,
  maxMs = BACKOFF_MAX_MS,
) {
  const n = Number.isFinite(attempt) && attempt > 1 ? Math.floor(attempt) : 1;
  return Math.min(maxMs, baseMs * 2 ** (n - 1));
}

export function createSessionRefreshScheduler({
  getExpiryMs,
  refresh,
  onExpired,
  isActive = () => true,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
}) {
  let gen = 0;
  let timerId = null;
  let inFlight = null;
  let attempts = 0;

  const clear = () => {
    if (timerId !== null) {
      clearTimer(timerId);
      timerId = null;
    }
  };
  const arm = (delay) => {
    clear();
    const captured = gen;
    timerId = setTimer(() => {
      timerId = null;
      if (captured === gen) run();
    }, Math.max(0, delay));
  };
  const expiryOf = () => {
    const v = getExpiryMs();
    return Number.isFinite(v) ? v : null;
  };
  // backoff 시도 상한을 소진해도 만료 감시는 남긴다 — resume 이벤트도 보호 요청도 없는 유휴 탭이
  //  만료 시각을 조용히 지나쳐 만료된 세션으로 남는 구멍을 막는다. 갱신은 더 시도하지 않고 만료만 알린다.
  const armExpiryBoundary = (exp) => {
    clear();
    const captured = gen;
    timerId = setTimer(
      () => {
        timerId = null;
        if (captured !== gen || !isActive()) return;
        const currentExpiry = expiryOf();
        if (currentExpiry !== null && currentExpiry > now()) rearm();
        else onExpired();
      },
      Math.max(0, exp - now()),
    );
  };

  // floorMs: 갱신 직후 재예약 — 지연이 0 이어도 즉시 재실행하지 않는다
  const rearm = (floorMs = 0) => {
    clear();
    const exp = expiryOf();
    if (exp === null || !isActive()) return;
    const delay = computeRefreshDelay(exp, now());
    if (delay <= 0 && floorMs <= 0) {
      run();
      return;
    }
    arm(Math.max(delay, floorMs));
  };

  function run() {
    if (!isActive()) { clear(); return Promise.resolve(); }
    if (inFlight) return inFlight;
    const captured = gen;
    clear();
    inFlight = Promise.resolve()
      .then(() => refresh())
      .then(
        () => {
          if (captured !== gen) return;
          attempts = 0;
          rearm(MIN_RESCHEDULE_MS);
        },
        () => {
          // 실패 사유(Google 오류·원시 credential)는 여기서 읽지도 기록하지도 않는다
          if (captured !== gen || !isActive()) return;
          const exp = expiryOf();
          if (exp === null) return;
          if (now() >= exp) {
            onExpired();
            return;
          }
          attempts += 1;
          if (attempts >= BACKOFF_MAX_ATTEMPTS) {
            armExpiryBoundary(exp);
            return;
          }
          arm(Math.min(computeBackoffDelay(attempts), exp - now()));
        },
      )
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  }

  return {
    schedule: () => rearm(),
    resume: () => {
      const exp = expiryOf();
      if (exp === null || !isActive()) return;
      if (exp - now() <= REFRESH_LEAD_MS) run();
      else rearm();
    },
    stop: () => {
      gen += 1;
      attempts = 0;
      clear();
    },
  };
}
